import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, AlertCircle, ArrowLeft } from "lucide-react";
import { resolvePostLoginPath } from "../lib/authRedirect";
const MAX_FAILED_ATTEMPTS = 3;
const LOCKOUT_SECONDS = 30;
const LOCAL_LOCKOUT_STORAGE_KEY = "echo_jham_login_lockout";

const normalizeEmailKey = (email) => String(email || "").trim().toLowerCase();
const getRpcPayload = (data) => (Array.isArray(data) ? data[0] || {} : data || {});
const getLockoutSeconds = (payload) => {
  const seconds = Number(payload?.seconds_remaining);
  if (Number.isFinite(seconds) && seconds > 0) {
    return Math.ceil(seconds);
  }

  const minutes = Number(payload?.minutes_remaining);
  if (Number.isFinite(minutes) && minutes > 0) {
    return Math.ceil(minutes * 60);
  }

  return 0;
};

const readLocalLockoutStore = () => {
  try {
    const raw = localStorage.getItem(LOCAL_LOCKOUT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeLocalLockoutStore = (store) => {
  localStorage.setItem(LOCAL_LOCKOUT_STORAGE_KEY, JSON.stringify(store));
};

const clearLocalLockout = (email) => {
  const normalizedEmail = normalizeEmailKey(email);
  if (!normalizedEmail) return;

  const store = readLocalLockoutStore();
  if (!(normalizedEmail in store)) return;

  delete store[normalizedEmail];
  writeLocalLockoutStore(store);
};

const getLocalLockoutState = (email) => {
  const normalizedEmail = normalizeEmailKey(email);
  if (!normalizedEmail) {
    return { isLocked: false, secondsRemaining: 0, attemptsRemaining: MAX_FAILED_ATTEMPTS };
  }

  const store = readLocalLockoutStore();
  const current = store[normalizedEmail] || {};
  const failedAttempts = Number(current.failedAttempts || 0);
  const lockUntil = Number(current.lockUntil || 0);
  const now = Date.now();

  if (lockUntil > now) {
    return {
      isLocked: true,
      secondsRemaining: Math.ceil((lockUntil - now) / 1000),
      attemptsRemaining: 0,
    };
  }

  if (lockUntil) {
    delete store[normalizedEmail];
    writeLocalLockoutStore(store);
    return { isLocked: false, secondsRemaining: 0, attemptsRemaining: MAX_FAILED_ATTEMPTS };
  }

  return {
    isLocked: false,
    secondsRemaining: 0,
    attemptsRemaining: Math.max(0, MAX_FAILED_ATTEMPTS - failedAttempts),
  };
};

const recordLocalFailedAttempt = (email) => {
  const normalizedEmail = normalizeEmailKey(email);
  if (!normalizedEmail) {
    return { isLocked: false, attemptsRemaining: MAX_FAILED_ATTEMPTS, secondsRemaining: 0 };
  }

  const store = readLocalLockoutStore();
  const current = store[normalizedEmail] || {};
  const now = Date.now();
  const currentLockUntil = Number(current.lockUntil || 0);

  if (currentLockUntil > now) {
    return {
      isLocked: true,
      attemptsRemaining: 0,
      secondsRemaining: Math.ceil((currentLockUntil - now) / 1000),
    };
  }

  let failedAttempts = Number(current.failedAttempts || 0) + 1;
  let lockUntil = 0;

  if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
    lockUntil = now + LOCKOUT_SECONDS * 1000;
    failedAttempts = 0;
  }

  store[normalizedEmail] = {
    failedAttempts,
    lockUntil,
  };
  writeLocalLockoutStore(store);

  if (lockUntil > now) {
    return {
      isLocked: true,
      attemptsRemaining: 0,
      secondsRemaining: Math.ceil((lockUntil - now) / 1000),
    };
  }

  return {
    isLocked: false,
    attemptsRemaining: Math.max(0, MAX_FAILED_ATTEMPTS - failedAttempts),
    secondsRemaining: 0,
  };
};

const checkServerLockout = async (email) => {
  const { data, error } = await supabase.rpc("check_login_lockout", {
    p_email: email,
  });

  if (error) throw error;
  return getRpcPayload(data);
};

const recordServerFailedAttempt = async (email) => {
  const { data, error } = await supabase.rpc("record_failed_login_attempt", {
    p_email: email,
    p_max_attempts: MAX_FAILED_ATTEMPTS,
    p_lockout_seconds: LOCKOUT_SECONDS,
  });

  if (error) throw error;
  return getRpcPayload(data);
};

const clearServerFailedAttempts = async (email) => {
  const { error } = await supabase.rpc("clear_login_failures", {
    p_email: email,
  });

  if (error) throw error;
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendFeedback, setResendFeedback] = useState("");
  const [lockoutSecondsLeft, setLockoutSecondsLeft] = useState(0);
  const [lockoutEmail, setLockoutEmail] = useState("");
  const [formData, setFormData] = useState({ email: "", password: "" });

  useEffect(() => {
    if (lockoutSecondsLeft <= 0) return;

    const timer = window.setInterval(() => {
      setLockoutSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [lockoutSecondsLeft]);

  const startLockoutCountdown = (seconds, email) => {
    const normalizedSeconds = Math.max(0, Math.ceil(Number(seconds) || 0));
    if (!normalizedSeconds) return;
    setLockoutSecondsLeft(normalizedSeconds);
    setLockoutEmail(normalizeEmailKey(email));
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    setResendFeedback("");

    const targetEmail = (resendEmail || formData.email).trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(targetEmail)) {
      setResendFeedback("Please enter a valid email address to resend verification.");
      return;
    }

    setResendLoading(true);
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: "signup",
        email: targetEmail,
        options: {
          emailRedirectTo: `${process.env.REACT_APP_SITE_URL || window.location.origin}/auth/callback`
        }
      });

      if (resendError) throw resendError;

      setResendFeedback("A new verification link has been sent. Please check your inbox.");
      setError("");
    } catch (resendErr) {
      setResendFeedback(resendErr.message || "Failed to resend verification email.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const normalizedEmail = normalizeEmailKey(formData.email);
    const localLockoutInfo = getLocalLockoutState(normalizedEmail);

    if (localLockoutInfo.isLocked) {
      startLockoutCountdown(localLockoutInfo.secondsRemaining, normalizedEmail);
      setError(`Too many failed attempts. Try again in ${localLockoutInfo.secondsRemaining}s.`);
      setLoading(false);
      return;
    }

    const isLockedForCurrentEmail =
      lockoutSecondsLeft > 0 && normalizeEmailKey(formData.email) === lockoutEmail;

    if (isLockedForCurrentEmail) {
      setError(`Too many failed attempts. Try again in ${lockoutSecondsLeft}s.`);
      setLoading(false);
      return;
    }

    try {
      const lockoutInfo = await checkServerLockout(normalizedEmail);
      if (lockoutInfo?.is_locked) {
        const secondsRemaining = getLockoutSeconds(lockoutInfo) || LOCKOUT_SECONDS;
        startLockoutCountdown(secondsRemaining, normalizedEmail);
        setError(`Too many failed attempts. Try again in ${secondsRemaining}s.`);
        setLoading(false);
        return;
      }
    } catch (lockCheckErr) {
      console.warn("Server-side lockout check unavailable:", lockCheckErr.message);
    }

    try {
      // Sign in with Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: formData.password
      });

      if (authError) throw authError;

      const user = authData.user;
      setLockoutSecondsLeft(0);
      setLockoutEmail("");
      clearLocalLockout(normalizedEmail);

      try {
        await clearServerFailedAttempts(normalizedEmail);
      } catch (clearErr) {
        console.warn("Unable to clear server-side failed login attempts:", clearErr.message);
      }

      const { data: userRow, error: userStatusError } = await supabase
        .from("users")
        .select("status")
        .eq("id", user.id)
        .maybeSingle();

      if (userStatusError) {
        console.warn("User status check failed during login:", userStatusError.message);
      }

      if (String(userRow?.status || "").toLowerCase() === "inactive") {
        setError("Account is inactive. Contact support.");
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      // Check if email is confirmed
      if (!user.email_confirmed_at) {
        setError("Please verify your email first");
        setShowResend(true);
        setResendEmail(normalizedEmail);
        // Sign out the user if email is not verified
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      // SUCCESS: Proceed to app
      localStorage.setItem("userAuthenticated", "true");
      navigate(resolvePostLoginPath(location.state?.from), { replace: true });
    } catch (err) {
      console.error("Login Error:", err.message);
      if (err.message.includes("Invalid login credentials")) {
        try {
          const attemptInfo = await recordServerFailedAttempt(normalizedEmail);
          if (attemptInfo?.is_locked) {
            const secondsRemaining = getLockoutSeconds(attemptInfo) || LOCKOUT_SECONDS;
            startLockoutCountdown(secondsRemaining, normalizedEmail);
            setError(`Too many failed attempts. Try again in ${secondsRemaining}s.`);
          } else {
            const remaining = Number(attemptInfo?.attempts_remaining ?? (MAX_FAILED_ATTEMPTS - 1));
            setError(`Incorrect email or password. ${remaining} attempt(s) remaining.`);
          }
        } catch (attemptErr) {
          console.warn("Server-side failed-attempt recording unavailable:", attemptErr.message);
          const localAttempt = recordLocalFailedAttempt(normalizedEmail);
          if (localAttempt.isLocked) {
            startLockoutCountdown(localAttempt.secondsRemaining, normalizedEmail);
            setError(`Too many failed attempts. Try again in ${localAttempt.secondsRemaining}s.`);
          } else {
            setError(`Incorrect email or password. ${localAttempt.attemptsRemaining} attempt(s) remaining before temporary lockout.`);
          }
        }
      } else {
        setError(err.message || "An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const isLockedForCurrentEmail =
    lockoutSecondsLeft > 0 && normalizeEmailKey(formData.email) === lockoutEmail;

  return (
    <div className="min-h-screen flex bg-[#f3f2f1]">
      <div className="hidden md:flex w-1/2 bg-black items-center justify-center p-12">
        <div className="text-center">
          <img src="/assets/echojham.logo3.png" alt="Echo Jham" className="w-64 mb-3" />
        </div>
      </div>

      <div className="w-full md:w-1/2 flex items-center justify-center p-8 auth-page-enter">
        <div className="w-full max-w-sm">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mb-6 inline-flex items-center gap-2 text-xs font-black uppercase tracking-wide text-gray-600 hover:text-brand-maroon"
          >
            <ArrowLeft size={14} /> Back to Home
          </button>

          <div className="mb-10">
            <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase italic">Welcome Back</h1>
            <p className="text-sm text-gray-500 font-medium">Log in to your account.</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs font-bold rounded-r-xl flex items-center gap-3">
              <AlertCircle size={18} /> {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <Input icon={<Mail size={16} />} placeholder="Email Address" name="email" type="email" onChange={handleChange} />
            <Input 
              icon={<Lock size={16} />} 
              placeholder="Password" 
              name="password" 
              type={showPassword ? "text" : "password"} 
              onChange={handleChange}
              isPassword
              showPassword={showPassword}
              togglePassword={() => setShowPassword(!showPassword)}
            />

            {isLockedForCurrentEmail && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-red-700">
                Too many failed attempts. Please wait {lockoutSecondsLeft}s before trying again.
              </div>
            )}

            <button disabled={loading || isLockedForCurrentEmail} className="w-full mt-6 bg-brand-maroon hover:bg-red-700 text-white py-4 rounded-full text-xs font-black tracking-widest uppercase shadow-xl transition active:scale-95 disabled:opacity-50">
              {loading ? "Checking..." : isLockedForCurrentEmail ? `Locked (${lockoutSecondsLeft}s)` : "Sign In"}
            </button>
          </form>

          <Link
            to="/reset-password"
            className="mt-4 inline-block text-xs font-bold uppercase tracking-wide text-gray-600 hover:text-brand-maroon"
          >
            Forgot Password?
          </Link>

          {showResend && (
            <form onSubmit={handleResendVerification} className="mt-6 p-4 border border-gray-200 rounded-2xl bg-white/70 space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-700">Need a new verification email?</p>
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="Enter your registered email"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-maroon/20 focus:border-brand-maroon text-black"
              />
              <button
                type="submit"
                disabled={resendLoading}
                className="w-full bg-black text-white py-3 rounded-xl text-[11px] font-black uppercase tracking-wider hover:bg-brand-maroon transition disabled:opacity-50"
              >
                {resendLoading ? "Resending..." : "Resend Verification Link"}
              </button>
              {resendFeedback ? <p className="text-[11px] text-gray-600">{resendFeedback}</p> : null}
            </form>
          )}

          <p className="text-center text-sm font-medium text-gray-500 mt-10">
            New here? <Link to="/signup" className="ml-2 text-brand-maroon font-black italic hover:underline">Create Account</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const Input = ({ icon, isPassword, showPassword, togglePassword, ...props }) => (
  <div className="relative group">
    <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-maroon transition-colors">{icon}</span>
    <input {...props} required className="w-full pl-14 pr-12 py-4 rounded-2xl border border-gray-200 bg-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-maroon text-black" />
    {isPassword && (
      <button type="button" onClick={togglePassword} className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400">
        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    )}
  </div>
);

export default Login;